/**
 * Área "Sistema": a aba de parâmetros do admin atual, com as 11 sub-abas. Porte de
 * `portal/admin.html:1915-2194` (tela) e `6455-6480` (abrir e trocar de sub-aba); linhas 70 a 85 e
 * 117 a 144 do inventário (`docs/PARIDADE-ADMIN.md`).
 *
 * SÓ O ADMINISTRADOR VÊ: o menu já esconde a área dos outros papéis (`papeis.ts`), e esta tela não
 * desenha nada para quem não é administrador, mesmo que alguém chegue à rota por outro caminho
 * (`admin.html:3462-3467`). Quem decide de verdade é o RLS (ADR 0002); a tela só não mostra o que
 * daria erro.
 *
 * O GESTO DE ABRIR, igual ao legado: abrir a área lê as 10 sub-abas de uma vez (`admin.html:6455`),
 * a primeira ("Usuários") aparece, e clicar numa sub-aba mostra aquela e a relê
 * (`admin.html:6472-6480`). "Cliente Comunidade" não tem leitura. As sub-abas escondidas continuam
 * montadas, como os `div` escondidos do legado: o que foi digitado numa não se perde ao ir a outra.
 */

// Estado e memória do React.
import { useCallback, useState, type ReactNode } from 'react';
// O botão da plataforma.
import { Botao } from '@complexity/ui';
// Sessão e papel.
import { usePainel } from '../../contexto.js';
// A confirmação e o aviso do legado, desenhados uma vez para a área inteira.
import { useConfirmacao } from '../../comum/confirmacao.js';
// As 11 sub-abas, na ordem do legado.
import { SUB_ABAS } from './catalogos.js';
// A memória da área, que guarda a sub-aba escolhida entre uma visita e outra.
import { useMemoria } from './memoria.js';
// Cada sub-aba.
import { SubAbaUsuarios } from './usuarios.js';
import { SubAbaVisibilidade } from './visibilidade.js';
import { SubAbaVinculos } from './vinculos.js';
import { SubAbaEmailProduto } from './email-produto.js';
import { SubAbaPrecos } from './precos.js';
import { SubAbaManualFoto } from './manual-foto.js';
import { SubAbaRoteiro } from './roteiro.js';
import { SubAbaPessoasIndicacao } from './pessoas-indicacao.js';
import { SubAbaGiftCards } from './gift-cards.js';
import { SubAbaCupons } from './cupons.js';
import { SubAbaClienteComunidade } from './cliente-comunidade.js';

export function TelaSistema() {
  const { acesso } = usePainel();
  // Só "administrador" vê os parâmetros (`papelAtual !== 'administrador'` esconde a aba).
  if (acesso.papel !== 'administrador') return null;
  return <SistemaDoAdministrador />;
}

function SistemaDoAdministrador() {
  // A sub-aba visível; a primeira abre por padrão (`param-sub-ativo` no HTML do legado). Fica na
  // memória da área porque o legado não mexe em `param-sub-ativo` ao trocar de aba principal
  // (`admin.html:6440-6456`): quem sai do Sistema e volta cai na mesma sub-aba em que estava.
  const [ativa, setAtiva] = useMemoria('sistema:sub-aba-ativa', SUB_ABAS[0]!.id);
  // Um contador por sub-aba: cada clique soma um, e a sub-aba relê (`CARREGADOR_PARAM_SUB`).
  const [recargas, setRecargas] = useState<Readonly<Record<string, number>>>({});
  // O mapa de visibilidade por aba, que "Visibilidade Aba" lê e "Vínculo Produto Aba" usa. Também na
  // memória: no legado é a variável `visibilidadeAbasCarregadas`, que vive com a página, e é ela que
  // mantém o campo "Visibilidade" do vínculo escrito enquanto a releitura da volta não termina.
  const [mapaDeVisibilidade, setMapaDeVisibilidade] = useMemoria<Readonly<Record<string, string>>>('sistema:mapa-visibilidade', {});
  // A confirmação e o aviso, com o `dialogo` desenhado uma vez no fim.
  const { confirmar, alertar, dialogo } = useConfirmacao();

  // O clique numa sub-aba: mostra e relê (`admin.html:6473-6479`).
  const abrir = useCallback(
    (id: string) => {
      setAtiva(id);
      setRecargas((atuais) => ({ ...atuais, [id]: (atuais[id] ?? 0) + 1 }));
    },
    [setAtiva],
  );

  // A leitura e a gravação de "Visibilidade Aba" alimentam o mapa do vínculo.
  const aoLerMapa = useCallback((mapa: Readonly<Record<string, string>>) => setMapaDeVisibilidade(mapa), [setMapaDeVisibilidade]);
  const aoSalvarAba = useCallback(
    (aba: string, visibilidade: string) => setMapaDeVisibilidade((atual) => ({ ...atual, [aba]: visibilidade })),
    [setMapaDeVisibilidade],
  );

  // O que toda sub-aba com lista recebe.
  const comum = (id: string) => ({ confirmar, alertar, recarga: recargas[id] ?? 0 });

  // Cada sub-aba montada uma vez, e mostrada ou escondida pelo `hidden`.
  const conteudos: Readonly<Record<string, ReactNode>> = {
    'param-sub-administradores': <SubAbaUsuarios {...comum('param-sub-administradores')} />,
    'param-sub-visibilidade': (
      <SubAbaVisibilidade recarga={recargas['param-sub-visibilidade'] ?? 0} aoLerMapa={aoLerMapa} aoSalvarAba={aoSalvarAba} />
    ),
    'param-sub-vinculos': <SubAbaVinculos {...comum('param-sub-vinculos')} mapaDeVisibilidade={mapaDeVisibilidade} />,
    'param-sub-email': <SubAbaEmailProduto {...comum('param-sub-email')} />,
    'param-sub-precos': <SubAbaPrecos recarga={recargas['param-sub-precos'] ?? 0} />,
    'param-sub-manual-foto': <SubAbaManualFoto {...comum('param-sub-manual-foto')} />,
    'param-sub-roteiro-produtos': <SubAbaRoteiro recarga={recargas['param-sub-roteiro-produtos'] ?? 0} />,
    'param-sub-pessoas-indicacao': <SubAbaPessoasIndicacao {...comum('param-sub-pessoas-indicacao')} />,
    'param-sub-gift-cards': <SubAbaGiftCards {...comum('param-sub-gift-cards')} />,
    'param-sub-cupons': <SubAbaCupons {...comum('param-sub-cupons')} />,
    'param-sub-cliente-comunidade': <SubAbaClienteComunidade />,
  };

  return (
    <div className="flex min-w-0 flex-col gap-cx-6">
      {/*
        A SUB-NAVEGAÇÃO QUEBRA EM LINHAS, e não rola mais de lado.

        O DEFEITO QUE ISTO CONSERTA, apontado pelo fundador em 07/10/2026 com a captura da barra:
        *"está cortando os menus quando nao cabe na tela"*. O desenho anterior copiava o legado
        (`.param-sub-nav`, `admin.html:532-560`): uma linha só, com `overflow-x-auto` e a barra de
        rolagem ESCONDIDA. Ele rolava, mas sem nenhum sinal de que rolava, e a última aba aparecia
        fatiada na borda. Rolagem invisível é conteúdo escondido: quem não sabe que pode arrastar
        não encontra "Cliente Comunidade".

        QUEBRAR É MELHOR QUE ROLAR AQUI porque são onze abas curtas, e não uma lista longa: em
        qualquer largura elas cabem em duas ou três linhas, e TODAS ficam visíveis de uma vez, sem
        gesto nenhum. `gap-y` menor que o `gap-x` mantém as linhas juntas o bastante para a barra
        continuar lendo como um bloco só.
      */}
      <nav
        aria-label="Parâmetros do sistema"
        className="flex flex-wrap gap-x-cx-5 gap-y-cx-1 border-b border-borda"
      >
        {SUB_ABAS.map((sub) => {
          const escolhida = sub.id === ativa;
          return (
            <Botao
              key={sub.id}
              variante="fantasma"
              tamanho="sm"
              data-sub={sub.id}
              aria-current={escolhida ? 'page' : undefined}
              className={`shrink-0 whitespace-nowrap rounded-none border-b-2 px-0 text-cx-sm ${
                escolhida ? '!text-texto border-b-marca font-semibold' : 'hover:border-b-borda'
              }`}
              onClick={() => abrir(sub.id)}
            >
              {sub.rotulo}
            </Botao>
          );
        })}
      </nav>

      {SUB_ABAS.map((sub) => (
        <section key={sub.id} id={sub.id} hidden={sub.id !== ativa} aria-label={sub.rotulo}>
          {conteudos[sub.id]}
        </section>
      ))}

      {dialogo}
    </div>
  );
}
